import { useEffect } from 'react'
import { CAPTURE_ATTRIBUTION } from 'api/analytics/capture-attribution'
import { analyticsClient } from './analytics-client'
import { captureAttribution, toCaptureAttributionInput } from './attribution'
import {
  clearStoredAttribution,
  readStoredAttribution,
  storeAttribution
} from './attribution-storage'
import { sendAnalyticsOperation } from './transport'
import type { SendOptions, SendResult } from './transport'

/**
 * Reports the buffered attribution. The buffer goes once the backend has it
 * or refused it (`drop`); only a `retry` keeps it for a later attempt.
 */
const sendStoredAttribution = async (
  options: SendOptions
): Promise<SendResult> => {
  const attribution = readStoredAttribution()
  if (!attribution) return 'ok'

  const result = await sendAnalyticsOperation(
    CAPTURE_ATTRIBUTION,
    { input: toCaptureAttributionInput(attribution) },
    options
  )
  if (result !== 'retry') clearStoredAttribution()

  return result
}

/**
 * Captures the attribution of this page load once (FE-320).
 *
 * Every capture is a touch: it replaces the tab's buffer and is reported with
 * `captureAttribution` ahead of the next events batch, for whoever the
 * requests go out as — the backend snapshots the attribution into the events
 * it journals. The buffer stays until the backend answers, so a touch that
 * did not go out before the page unloaded (an OAuth redirect right after
 * landing, the network) is reported on the next page load of the tab. A
 * refused call (`drop`: a 4xx or a validation error) clears it too — a later
 * page load would send the same refused payload again.
 */
export const useCaptureAttribution = () => {
  useEffect(() => {
    const captured = captureAttribution({
      search: window.location.search,
      href: window.location.href,
      origin: window.location.origin,
      referrer: document.referrer
    })
    if (captured) storeAttribution(captured)

    if (readStoredAttribution()) {
      analyticsClient.runBefore('attribution', sendStoredAttribution)
    }
  }, [])
}
